if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement =
              responseHTML.querySelector('product-info[id^="MainProduct-"]') ||
              responseHTML.querySelector('product-info');
            if (!productElement) return;

            const sectionId = productElement.dataset.section;
            // SMA — the product section's own inline script (variant cards, most-picked default,
            // name on tin) sits outside <product-info>. Grab it before the IDs are rewritten.
            const sectionScripts = this.getSectionScripts(productElement, sectionId);

            this.preprocessHTML(productElement);

            // SMA — the product page styles are scoped to #shopify-section-<section id>. Rebuild
            // that wrapper (with the quick add ID) so every one of them applies inside the modal.
            HTMLUpdateUtility.setInnerHTML(
              this.modalContent,
              `<div id="shopify-section-quickadd-${sectionId}" class="quick-add-modal__section">${productElement.outerHTML}</div>`
            );
            const sectionWrapper = this.modalContent.firstElementChild;

            // SMA — wait for the product page stylesheets so the modal never flashes unstyled.
            return this.waitForStylesheets(this.modalContent).then(() => {
              if (!sectionWrapper.isConnected) return;

              if (window.Shopify && Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
              }
              if (window.ProductModel) window.ProductModel.loadShopifyXR();

              this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

              super.show(opener);
              this.runSectionScripts(sectionScripts, sectionWrapper);
            });
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      /*
        SMA — inline scripts that belong to the product section but live outside <product-info>.
        Their section ID references are rewritten to the quick add ID, like the markup.
      */
      getSectionScripts(productElement, sectionId) {
        const section = productElement.closest('.shopify-section');
        if (!section || !sectionId) return [];

        return Array.from(section.children)
          .filter(
            (element) =>
              element.tagName === 'SCRIPT' &&
              !element.src &&
              (!element.type || element.type === 'text/javascript' || element.type === 'module')
          )
          .map((script) => ({
            type: script.type,
            code: script.textContent.replaceAll(sectionId, `quickadd-${sectionId}`),
          }));
      }

      // Runs them once product-info.js / global.js are ready, exactly as on the product page.
      runSectionScripts(scripts, sectionWrapper) {
        if (!scripts.length) return;

        Promise.all([customElements.whenDefined('product-info'), customElements.whenDefined('variant-selects')]).then(
          () => {
            // The modal was closed (or reopened with another product) in the meantime.
            if (!sectionWrapper.isConnected) return;

            scripts.forEach(({ type, code }) => {
              const script = document.createElement('script');
              if (type) script.type = type;
              script.textContent = code;
              sectionWrapper.appendChild(script);
            });
          }
        );
      }

      waitForStylesheets(container) {
        const links = Array.from(container.querySelectorAll('link[rel="stylesheet"]')).filter(
          (link) => link.media !== 'print'
        );

        const loads = links.map(
          (link) =>
            new Promise((resolve) => {
              if (link.sheet) return resolve();
              link.addEventListener('load', resolve, { once: true });
              link.addEventListener('error', resolve, { once: true });
            })
        );

        // Never hold the modal back for more than 3s.
        return Promise.race([Promise.all(loads), new Promise((resolve) => setTimeout(resolve, 3000))]);
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeContentAfterBuyButtons(productElement);
        this.showFullGallery(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // SMA — the mobile sticky Add to cart bar belongs to the product page only.
        productElement.querySelectorAll('sma-sticky-atc').forEach((element) => element.remove());
      }

      /*
        SMA — the modal shows the product page from the top down to the buy buttons.
        Every block after the buy buttons block (and the "View full details" link) is dropped.
      */
      removeContentAfterBuyButtons(productElement) {
        const infoContainer = productElement.querySelector('.product__info-container');
        const submitButton = infoContainer?.querySelector('[id^="ProductSubmitButton-"]');
        if (!submitButton) return;

        let buyButtonsBlock = submitButton;
        while (buyButtonsBlock && buyButtonsBlock.parentElement !== infoContainer) {
          buyButtonsBlock = buyButtonsBlock.parentElement;
        }
        if (!buyButtonsBlock) return;

        while (buyButtonsBlock.nextElementSibling) {
          buyButtonsBlock.nextElementSibling.remove();
        }
      }

      // SMA — keep the gallery exactly as on the product page: slider dots/arrows and thumbnails.
      // (Zoom toggles stay hidden — the zoom popup isn't part of the modal.)
      showFullGallery(productElement) {
        productElement
          .querySelectorAll(
            'media-gallery .slider-buttons.quick-add-hidden, media-gallery .thumbnail-slider.quick-add-hidden'
          )
          .forEach((element) => element.classList.remove('quick-add-hidden'));
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}